import { Injectable, inject } from '@angular/core';
import {
  DocumentData,
  DocumentReference,
  Firestore,
  QueryConstraint,
  Transaction,
  WhereFilterOp,
  addDoc,
  collection,
  collectionData,
  deleteDoc,
  doc,
  docData,
  getDocs,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  private firestore = inject(Firestore);

  /**
   * Return the Firestore instance
   */
  getFirestoreInstance(): Firestore {
    return this.firestore;
  }

  /**
   * Get a collection as an observable
   */
  getCollection<T extends DocumentData>(path: string): Observable<T[]> {
    const colRef = collection(this.firestore, path);
    return collectionData(colRef, { idField: 'id' }) as Observable<T[]>;
  }

  /**
   * Get a collection with querying
   */
  getCollectionWithQuery<T extends DocumentData>(
    path: string,
    field: string,
    operator: WhereFilterOp,
    value: string | number | boolean | null,
  ): Observable<T[]> {
    const colRef = collection(this.firestore, path);
    const q = query(colRef, where(field, operator, value));
    return collectionData(q, { idField: 'id' }) as Observable<T[]>;
  }

  /**
   * Get a single document
   */
  getDoc<T extends DocumentData>(
    path: string,
    id: string,
  ): Observable<T | undefined> {
    const docRef = doc(this.firestore, `${path}/${id}`) as DocumentReference<
      T,
      DocumentData
    >;
    return docData(docRef, { idField: 'id' });
  }

  /**
   * Create a new document in a collection
   */
  async createDoc<T extends DocumentData>(path: string, data: T) {
    const colRef = collection(this.firestore, path);
    return await addDoc(colRef, data);
  }

  /**
   * Set a document by ID
   */
  async setDoc<T extends DocumentData>(
    path: string,
    id: string,
    data: T,
    options?: { merge?: boolean },
  ) {
    const docRef = doc(this.firestore, `${path}/${id}`) as any;
    if (options) {
      return await setDoc(docRef, data, options);
    }
    return await setDoc(docRef, data);
  }

  /**
   * Update a document by ID
   */
  async updateDoc<T extends DocumentData>(
    path: string,
    id: string,
    data: Partial<T>,
  ) {
    const docRef = doc(this.firestore, `${path}/${id}`) as any;
    return await updateDoc(docRef, data as any);
  }

  /**
   * Delete a document by ID
   */
  async deleteDoc(path: string, id: string) {
    const docRef = doc(this.firestore, `${path}/${id}`);
    return await deleteDoc(docRef);
  }

  /**
   * Execute a Firestore transaction.
   * Use for atomic read-then-write operations where consistency is critical.
   * IMPORTANT: Only individual document reads (tx.get(docRef)) are allowed
   * inside the callback — collection queries will throw permission errors.
   */
  runTransaction<T>(updateFn: (tx: Transaction) => Promise<T>): Promise<T> {
    return runTransaction(this.firestore, updateFn);
  }

  /**
   * One-shot fetch of all documents where `field` is one of `values`.
   * Firestore's 'in' operator supports up to 30 values; this method auto-chunks
   * larger arrays and merges the results.
   * Returns a plain array — use when you need a snapshot, not a live stream.
   */
  async getDocsWhereIn<T extends DocumentData>(
    path: string,
    field: string,
    values: (string | number | boolean)[],
  ): Promise<T[]> {
    if (values.length === 0) return [];
    const colRef = collection(this.firestore, path);
    const results: T[] = [];
    // Chunk into batches of 30 (Firestore 'in' limit)
    for (let i = 0; i < values.length; i += 30) {
      const chunk = values.slice(i, i + 30);
      const constraints: QueryConstraint[] = [where(field, 'in', chunk)];
      const snap = await getDocs(query(colRef, ...constraints));
      snap.forEach(d => results.push({ id: d.id, ...d.data() } as unknown as T));
    }
    return results;
  }
}
